import React from 'react'
import { Box, Button, Chip, CircularProgress, Divider, Grid, LinearProgress, Paper, Stack, Typography, styled, useTheme } from '@mui/material'
import { ExitToApp, Folder, ManageAccounts, MultilineChart, RemoveCircle, SportsHockey, Work, EmojiEvents, TrendingUp, SwapHoriz, Group } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import PageBase from 'components/page-title/PageBase'
import LazyImage from 'components/shared-components/LazyImage'
import { ApiMutationKeys, ApiMutationService } from 'services/mutation/apiMutationService'
import { ApiQueryKeys, ApiQueryService } from 'services/query/apiQueryService'
import { useSnackbar } from 'hooks/notification/UseSnackbar'
import useFantaHockeyStore from 'hooks/store/FantaHockeyStateStore'
import DynamicIcon from 'components/helpers/DynamicIcon'
import { type YahooConnectedTeamProfile } from 'features/fantasy-team/models/yahooFantasyLeagueTeam'

const ProfileCard = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2,
  color: theme.palette.text.primary,
  boxShadow: theme.shadows[4],
  padding: theme.spacing(2)
}))

const CardContentWrapper = styled('div')(({ theme }) => ({
  gap: theme.spacing(2)
}))

const HeroCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2.5,
  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%)`,
  color: theme.palette.common.white
}))

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2,
  minHeight: 120
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 700
}))

const RankingBadge = ({ rank }: { rank: string | undefined }): JSX.Element => {
  const theme = useTheme()

  const getEndingForRank = (rank: string | undefined): string => {
    if (rank == null) return ''
    if (rank === '1') return 'st'
    if (rank === '2') return 'nd'
    if (rank === '3') return 'rd'
    return 'th'
  }

  const getRankingColor = (rank: string | undefined): string => {
    if (rank == null) return theme.palette.text.primary
    const r = Number.parseInt(rank)
    if (r <= 3) return theme.palette.success.main
    if (r <= 8) return theme.palette.info.main
    return theme.palette.error.main
  }

  const badgeGradient = `linear-gradient(90deg, ${getRankingColor(rank)} 0%, ${getRankingColor(rank)} 100%)`

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: badgeGradient,
        color: '#fff',
        fontWeight: 'bold',
        borderRadius: '9999px',
        py: 1,
        px: 2,
        boxShadow: '0 4px 15px rgba(0,0,0,0.25)'
      }}
    >
      <EmojiEvents sx={{ mr: 1 }} />
      <Typography variant="body1" component="span" sx={{ fontWeight: 'inherit' }}>
        {rank}
        {getEndingForRank(rank)} place
      </Typography>
    </Box>
  )
}

const ManageMyFantasyTeam: React.FC = () => {
  const store = useFantaHockeyStore()
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()

  const { isFetching, data: fantasyTeamProfile } = useQuery<YahooConnectedTeamProfile>({
    queryKey: [ApiQueryKeys.GetConnectedFantasyTeamProfileQuery],
    queryFn: async () => {
      return (await ApiQueryService[ApiQueryKeys.GetConnectedFantasyTeamProfileQuery]()).data
    },
    enabled: true
  })

  const { mutateAsync: deleteYahooFantasyTeam } = useMutation<boolean>({
    mutationKey: [ApiMutationKeys.DeleteYahooFantasyTeamMutation],
    mutationFn: async () => {
      return (await ApiMutationService[ApiMutationKeys.DeleteYahooFantasyTeamMutation]()).data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ApiQueryKeys.IsUserConnectedToYahooQuery] })
    }
  })

  const disconnectYahooFantasyTeam = async (): Promise<void> => {
    await deleteYahooFantasyTeam()
    store.setIsConnectedToYahooFantasy(false)
    store.setTradeSuggestions([])
    snackbar.openSnackbar(`Disconnected from ${fantasyTeamProfile?.team.name}`, 'success')
    navigate('/connect-my-fantasy-team', { replace: true })
  }

  const myTeamRow = fantasyTeamProfile?.league.standings?.teamList.teams.find(
    (team) => team.teamKey === fantasyTeamProfile?.team.teamKey
  )

  const wins = Number.parseInt(myTeamRow?.teamStandings?.outcomeTotals?.wins ?? '0')
  const losses = Number.parseInt(myTeamRow?.teamStandings?.outcomeTotals?.losses ?? '0')
  const total = wins + losses
  const percentage = myTeamRow?.teamStandings?.outcomeTotals?.percentage ?? '0'
  const pointsFor = myTeamRow?.teamStandings?.pointsFor
  const waiverPriority = myTeamRow?.waiverPriority
  const rank = myTeamRow?.teamStandings?.rank

  const normalise = (value: number): number => {
    const denom = Math.max(1, total)
    return (value * 100) / denom
  }

  return (
    <PageBase title={'Manage Team'} icon={Work}>
      <ProfileCard>
        <CardContentWrapper>
          {isFetching && (
            <Box my={4} display="flex" justifyContent="center">
              <CircularProgress size="40px" thickness={4} color="info" />
            </Box>
          )}

          {!isFetching && !(store.isConnectedToYahooFantasy ?? false) && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={700} mb={1}>
                Not connected to Yahoo! Fantasy
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Connect your team to unlock management features and insights.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => { navigate('/connect-my-fantasy-team', { replace: true }) }}
              >
                Connect My Fantasy Team
              </Button>
            </Paper>
          )}

          {!isFetching && (store.isConnectedToYahooFantasy ?? false) && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SectionTitle variant="h6">
                  <SportsHockey /> <Box ml={1} /> Fantasy League
                </SectionTitle>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={12}>
                <HeroCard elevation={3}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={'auto'}>
                      <LazyImage
                        borderRadius="16px"
                        height={'calc(0.25vw + 90px)'}
                        src={fantasyTeamProfile?.team.teamLogos.teamLogo.url}
                        alt={fantasyTeamProfile?.team.name}
                      />
                    </Grid>
                    <Grid item xs>
                      <Typography variant="overline" sx={{ opacity: 0.9 }}>
                        {fantasyTeamProfile?.league.name}
                      </Typography>
                      <Typography variant="h5" fontWeight={800}>
                        {fantasyTeamProfile?.team.name}
                      </Typography>
                      <Stack direction="row" spacing={0} mt={2} flexWrap="wrap">
                        <Chip
                          icon={<Folder sx={{ color: 'inherit' }} />}
                          label={`Waiver Priority: ${waiverPriority ?? '-'}`}
                          sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', mr: 1, mb: 1 }}
                        />
                        <Chip
                          icon={<TrendingUp sx={{ color: 'inherit' }} />}
                          label={`Season Points: ${pointsFor ?? '-'}`}
                          sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', mr: 1, mb: 1 }}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md="auto" textAlign={{ xs: 'left', md: 'right' }}>
                      <RankingBadge rank={rank} />
                    </Grid>
                  </Grid>
                </HeroCard>
              </Grid>

              <Grid item xs={12}>
                <SectionTitle variant="h6">
                  <MultilineChart /> <Box ml={1} /> Team Dashboard
                </SectionTitle>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={12} md={4}>
                <StatCard variant="outlined">
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Record
                  </Typography>
                  <Typography variant="h5" fontWeight={800}>
                    {wins} - {losses}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Win Percentage: {percentage}
                  </Typography>
                  <Box mt={1.5}>
                    <LinearProgress variant="determinate" color="success" value={normalise(wins)} />
                    <LinearProgress
                      sx={{ mt: 1 }}
                      variant="determinate"
                      color="error"
                      value={normalise(losses)}
                    />
                  </Box>
                </StatCard>
              </Grid>

              <Grid item xs={12} md={4}>
                <StatCard variant="outlined" sx={{ textAlign: 'center' }}>
                  <Box display="flex" justifyContent="center" gap={4} mb={1}>
                    <Typography variant="subtitle2">Trades</Typography>
                    <Typography variant="subtitle2">Moves</Typography>
                  </Box>
                  <Box display="flex" justifyContent="center" gap={8}>
                    <Typography variant="h5" fontWeight={800}>
                      {fantasyTeamProfile?.team.numberOfTrades ?? 0}
                    </Typography>
                    <Typography variant="h5" fontWeight={800}>
                      {fantasyTeamProfile?.team.numberOfMoves ?? 0}
                    </Typography>
                  </Box>
                  <Box mt={1} display="flex" justifyContent="center">
                    <SwapHoriz color="action" />
                  </Box>
                </StatCard>
              </Grid>

              <Grid item xs={12} md={4}>
                <StatCard variant="outlined">
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Team Managers
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {fantasyTeamProfile?.team.managerList?.managers.map((manager) => (
                      <Chip
                        key={manager.guid}
                        icon={<Group sx={{ color: 'inherit' }} />}
                        label={manager.nickname}
                        sx={{
                          fontWeight: 600,
                          color: '#ffffff',
                          bgcolor: (theme) => theme.palette.primary.light
                        }}
                      />
                    ))}
                  </Stack>
                </StatCard>
              </Grid>

              <Grid item xs={12}>
                <SectionTitle variant="h6">
                  <ManageAccounts /> <Box ml={1} /> Team Connection
                </SectionTitle>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<DynamicIcon Icon={ExitToApp} color="success" fontSizeMd={24} />}
                    onClick={() => {
                      navigate('/my-fantasy-team', { replace: true })
                    }}
                  >
                    Go to Yahoo! Fantasy Team
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DynamicIcon Icon={RemoveCircle} color="error" fontSizeMd={24} />}
                    onClick={() => {
                      void disconnectYahooFantasyTeam()
                    }}
                  >
                    Remove Yahoo! Fantasy Team
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          )}
        </CardContentWrapper>
      </ProfileCard>
    </PageBase>
  )
}

ManageMyFantasyTeam.displayName = 'ManageMyFantasyTeam'

export default React.memo(ManageMyFantasyTeam)
